/**
 * PriceChart — OHLCV candlestick / line chart for a fair-launch market.
 *
 * Fetches candles from GET /api/fair-launch/markets/:tokenAddress/candles
 * and renders candlestick bars (open/high/low/close) with a subtle close-price
 * area overlay, using a ComposedChart.  Timeframe toggles: 5m, 1h, 4h, 1d.
 *
 * Prices are stored as wei (NativeAmount strings); we convert to nanoETH
 * (÷ 1e9) for a readable Y-axis scale.
 *
 * Candlestick rendering
 * ─────────────────────
 * Each bar is rendered by CandlestickShape — a custom recharts Bar shape that
 * draws the high/low wick and the open/close body.  Bullish candles (close ≥
 * open) are green; bearish candles are red.  The close-price Area is kept as
 * a faint purple overlay so the trend line is still visible at a glance.
 *
 * Auto-refresh
 * ────────────
 * Candles are re-fetched every 30 seconds while the page is open so traders
 * see new trades without reloading.  Background refreshes do NOT show a
 * loading skeleton (no flicker) and do NOT reset the selected timeframe.
 * A successful background refresh clears a prior error state so the chart
 * recovers automatically once the API is healthy again.
 *
 * Page Visibility & Intersection Observer
 * ────────────────────────────────────────
 * When the browser tab is hidden the interval is suspended.  When the tab
 * becomes visible again a catch-up fetch fires immediately so the chart is
 * current before the user looks at it.
 *
 * Additionally, an IntersectionObserver watches the chart container.  If the
 * user navigates to a different route within the SPA — and the component stays
 * mounted but is scrolled out of view or hidden — the poll is also suspended.
 * A catch-up fetch fires the moment the chart re-enters the viewport so the
 * data is fresh before the user sees it.
 *
 * Stale-response safety
 * ─────────────────────
 * Foreground fetches (which show the spinner) and background fetches (silent)
 * use two SEPARATE monotonic request-id refs so they cannot invalidate each
 * other.  This prevents the race where a background poll fires while a
 * foreground fetch is in-flight, which could otherwise leave `loading` stuck
 * as `true` indefinitely.
 *
 *   fgRequestId — owned exclusively by fetchForeground; controls loading/error.
 *   bgRequestId — owned exclusively by fetchBackground; updates points silently.
 *
 * Token-address change guard
 * ──────────────────────────
 * When the parent navigates to a different market (tokenAddress prop changes),
 * a dedicated effect (declared before the initial-load effect) immediately
 * bumps BOTH counters.  This ensures any response that was in-flight for the
 * previous market is unconditionally discarded — even if it resolves in the
 * brief window before the new foreground fetch captures its own request id.
 * The guard is keyed on [tokenAddress, chainId] and is explicit and testable,
 * independent of the incidental counter bumps in the initial-load cleanup and
 * the background-poll teardown.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  ComposedChart,
  AreaChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// ── Types ─────────────────────────────────────────────────────────────────────

export type Bucket = "5m" | "1h" | "4h" | "1d";
type ChartMode = "candles" | "line";

/**
 * Returns true when a fetchBackground response should be discarded.
 *
 * Two independent guard conditions, either of which is sufficient to discard:
 *   Guard 1 (id):     a newer background request has started since this one —
 *                     i.e. the monotonic counter has moved past the captured id.
 *   Guard 2 (bucket): the user switched to a different timeframe since this
 *                     fetch was dispatched — even if no newer bg request exists
 *                     yet, the response belongs to the wrong bucket.
 *
 * Exported so each condition can be exercised in isolation in unit tests,
 * decoupled from the React effect that increments bgRequestId on bucket switch.
 */
export function isStaleBgResponse(
  requestId: number,
  currentBgRequestId: number,
  requestedBucket: Bucket,
  currentBucket: Bucket,
): boolean {
  return requestId !== currentBgRequestId || requestedBucket !== currentBucket;
}

interface RawCandle {
  /** API returns the bucket timestamp under the key "bucket" (ISO string or unix ms). */
  bucket: string | number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  tradeCount: number;
}

interface ChartPoint {
  time: number;       // unix ms
  price: number;      // close price in nanoETH
  open: number;
  high: number;
  low: number;
  volume: number;     // traded volume in nanoETH
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Background poll cadence in milliseconds. */
const POLL_INTERVAL_MS = 30_000;

/** Bullish / bearish candle colours. */
const COLOR_BULLISH = "#22c55e";
const COLOR_BEARISH = "#ef4444";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Convert a wei string to nanoETH (÷ 1e9) for display. */
function weiToNanoEth(wei: string): number {
  try {
    return Number(BigInt(wei)) / 1e9;
  } catch {
    return 0;
  }
}

/** Format a unix-ms timestamp relative to the chosen bucket. */
function formatTime(ms: number, bucket: Bucket): string {
  const d = new Date(ms);
  if (bucket === "1d") {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Format nanoETH price for the tooltip. */
function formatPrice(nano: number): string {
  if (nano === 0) return "—";
  return `${nano.toExponential(3)} nETH`;
}

/**
 * Format a number in compact shorthand without ever using scientific notation.
 * Uses toFixed(1) after scaling (toFixed never switches to exponential form),
 * then strips a redundant ".0" suffix.
 *
 * Examples: 0 → "0", 999 → "999", 1000 → "1k", 1500 → "1.5k",
 *           999_999 → "1000k" (promoted), 1_000_000 → "1M", 1_250_000 → "1.3M"
 *
 * Exported for unit-testing.
 */
export function formatCompact(n: number): string {
  if (n === 0) return "0";
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return n.toFixed(1).replace(/\.0$/, "");
}

/**
 * Return a human-readable age string for the "last updated" indicator.
 * Keeps the label short so it fits in the compact chart header.
 */
function formatAge(lastUpdatedAt: number, nowMs: number): string {
  const secs = Math.floor((nowMs - lastUpdatedAt) / 1_000);
  if (secs < 10) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ago`;
}

/** Parse a raw candle array into chart points, sorted ascending by time. */
export function parseCandles(candles: RawCandle[]): ChartPoint[] {
  return candles
    .map((c) => ({
      time:   typeof c.bucket === "number" ? c.bucket : new Date(c.bucket).getTime(),
      price:  weiToNanoEth(c.close),
      open:   weiToNanoEth(c.open),
      high:   weiToNanoEth(c.high),
      low:    weiToNanoEth(c.low),
      volume: weiToNanoEth(c.volume),
    }))
    .sort((a, b) => a.time - b.time);
}

// ── Candlestick shape ─────────────────────────────────────────────────────────
//
// recharts clones the element passed as `shape` to <Bar> and injects per-bar
// props: x, y, width, height, payload, and — critically — yAxis which carries
// the yAxis.scale() function used to convert domain values to pixel positions.

interface CandlestickShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartPoint;
  /** recharts injects this for Bar shapes rendered inside a ComposedChart. */
  yAxis?: { scale: (v: number) => number };
}

function CandlestickShape({
  x = 0,
  width = 0,
  payload,
  yAxis,
}: CandlestickShapeProps) {
  if (!payload || !yAxis?.scale) return null;

  const { open, high, low, price: close } = payload;
  const scale = yAxis.scale;

  const pxHigh  = scale(high);
  const pxLow   = scale(low);
  const pxOpen  = scale(open);
  const pxClose = scale(close);

  const isBullish = close >= open;
  const color     = isBullish ? COLOR_BULLISH : COLOR_BEARISH;

  const bodyTop    = Math.min(pxOpen, pxClose);
  const bodyBottom = Math.max(pxOpen, pxClose);
  const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
  const bodyWidth  = Math.max(width * 0.65, 2);
  const cx         = x + width / 2;

  return (
    <g data-testid={isBullish ? "candle-bullish" : "candle-bearish"}>
      {/* High–Low wick */}
      <line
        x1={cx}
        y1={pxHigh}
        x2={cx}
        y2={pxLow}
        stroke={color}
        strokeWidth={1}
      />
      {/* Open–Close body */}
      <rect
        x={cx - bodyWidth / 2}
        y={bodyTop}
        width={bodyWidth}
        height={bodyHeight}
        fill={color}
        stroke={color}
        strokeWidth={0.5}
        fillOpacity={0.85}
      />
    </g>
  );
}

// ── Volume bar shape ──────────────────────────────────────────────────────────
//
// Colours each volume bar green (bullish candle) or red (bearish candle) so
// traders can immediately see whether volume was buy-side or sell-side.

interface VolumeBarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartPoint;
}

function VolumeBarShape({ x = 0, y = 0, width = 0, height = 0, payload }: VolumeBarShapeProps) {
  if (!payload) return null;
  const isBullish = payload.price >= payload.open;
  const color     = isBullish ? COLOR_BULLISH : COLOR_BEARISH;
  // Leave a small gap between bars (10% each side) and ensure minimum 1 px height
  return (
    <rect
      data-testid={isBullish ? "vol-bar-bullish" : "vol-bar-bearish"}
      x={x + width * 0.1}
      y={y}
      width={Math.max(width * 0.8, 1)}
      height={Math.max(height, 1)}
      fill={color}
      fillOpacity={0.55}
    />
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

function ChartTooltip({
  active,
  payload,
  label,
  bucket,
}: {
  active?: boolean;
  payload?: { value: number; payload: ChartPoint }[];
  label?: number;
  bucket: Bucket;
}) {
  if (!active || !payload?.length || !payload[0]) return null;
  const pt = payload[0].payload;
  return (
    <div className="rounded-lg border border-line/40 bg-panel px-3 py-2 text-xs space-y-1 shadow-lg">
      <p className="text-muted/70 font-mono">{formatTime(label ?? pt.time, bucket)}</p>
      <p className="font-mono text-plasma">{formatPrice(pt.price)}</p>
      <div className="text-muted/60 space-y-0.5 pt-0.5">
        <p>H: {formatPrice(pt.high)}</p>
        <p>L: {formatPrice(pt.low)}</p>
        <p>O: {formatPrice(pt.open)}</p>
        {pt.volume > 0 && <p>Vol: {formatCompact(pt.volume)} nETH</p>}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface PriceChartProps {
  tokenAddress: string;
  chainId: number;
  symbol: string;
}

const BUCKETS: Bucket[] = ["5m", "1h", "4h", "1d"];

export function PriceChart({ tokenAddress, chainId, symbol }: PriceChartProps) {
  const [bucket, setBucket] = useState<Bucket>("1h");
  const [chartMode, setChartMode] = useState<ChartMode>(() => {
    try {
      const stored = localStorage.getItem("synapz:chartMode");
      if (stored === "candles" || stored === "line") return stored;
    } catch {
      // localStorage unavailable (SSR, private browsing with strict settings, etc.)
    }
    return "candles";
  });
  const [points, setPoints] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  /**
   * Unix-ms timestamp of the last successful foreground or background fetch.
   * Null until the first fetch resolves successfully (i.e. hidden during the
   * initial skeleton phase).
   */
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  /**
   * Monotonically-advancing "now" used to compute the age of `lastUpdatedAt`.
   * Ticks every 5 seconds — fine-grained enough for the ">90 s" staleness
   * threshold while avoiding unnecessary re-renders on every second.
   */
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  /**
   * Two separate monotonic counters — one per fetch path — so that a
   * background poll can never invalidate a foreground request and leave
   * `loading` stuck as true.
   *
   * fgRequestId: controlled by fetchForeground (shows spinner, sets error).
   * bgRequestId: controlled by fetchBackground (silent update, clears error).
   */
  const fgRequestId = useRef(0);
  const bgRequestId = useRef(0);

  /**
   * Stable ref so the interval / visibility callback can always read the
   * current bucket without restarting the interval on every timeframe switch.
   */
  const bucketRef = useRef(bucket);
  bucketRef.current = bucket;

  /**
   * Tracks whether the chart container is currently intersecting the viewport.
   * Updated by the IntersectionObserver below.  Starts as `true` so the
   * initial foreground fetch always fires (the observer hasn't attached yet).
   */
  const isIntersectingRef = useRef(true);

  /** Ref to the outermost chart container, observed by IntersectionObserver. */
  const chartRef = useRef<HTMLDivElement>(null);

  // ── "Now" ticker — advances every 5 s so the staleness badge stays current ─

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  // ── URL helper ─────────────────────────────────────────────────────────────

  const candlesUrl = useCallback(
    (b: Bucket) =>
      `/api/fair-launch/markets/${tokenAddress}/candles?chainId=${chainId}&bucket=${b}`,
    [tokenAddress, chainId],
  );

  // ── Foreground fetch (shows spinner, sets error) ───────────────────────────
  // Used on initial mount and every timeframe change.

  const fetchForeground = useCallback(async (b: Bucket) => {
    const id = ++fgRequestId.current;
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(candlesUrl(b));
      if (!res.ok) throw new Error("fetch failed");
      const data: { candles: RawCandle[] } = await res.json();
      if (id !== fgRequestId.current) return; // stale — discard
      setPoints(parseCandles(data.candles ?? []));
      setLastUpdatedAt(Date.now());
    } catch {
      if (id !== fgRequestId.current) return; // stale — discard
      setError(true);
      setPoints([]);
    } finally {
      // Only clear the spinner if this request is still the latest foreground
      // one AND a background fetch hasn't sneaked in (bgRequestId is
      // irrelevant here — the finally only touches loading).
      if (id === fgRequestId.current) setLoading(false);
    }
  }, [candlesUrl]);

  // ── Background fetch (silent, clears error on success) ────────────────────
  // Used by the 30-second interval and the visibility catch-up.
  //
  // Stale-response guard: captures both (a) a monotonic id and (b) the bucket
  // that was requested.  The response is discarded if either the id is no
  // longer current OR the user has since switched to a different timeframe.
  // This prevents an old-bucket response from overwriting data for the new
  // bucket after a rapid timeframe switch.

  const fetchBackground = useCallback(async (b: Bucket) => {
    const id = ++bgRequestId.current;
    try {
      const res = await fetch(candlesUrl(b));
      if (!res.ok) return; // silent failure — keep last known data
      const data: { candles: RawCandle[] } = await res.json();
      // Discard if a newer background request started, OR if the user has
      // switched to a different timeframe since this fetch was dispatched.
      if (isStaleBgResponse(id, bgRequestId.current, b, bucketRef.current)) return;
      setPoints(parseCandles(data.candles ?? []));
      // Successful background refresh recovers from a prior error state
      setError(false);
      setLastUpdatedAt(Date.now());
    } catch {
      // Silent failure — do not touch loading or error state
    }
  }, [candlesUrl]);

  // ── Token-address / chain change guard ────────────────────────────────────
  // Declared BEFORE the initial-load effect so it runs first in the same
  // React commit when tokenAddress or chainId changes.
  //
  // Bumping both counters here provides an explicit, targeted guard: any fetch
  // that was in-flight for the previous market is unconditionally stale before
  // the new foreground fetch even starts.  This is independent of (and
  // complementary to) the counter bumps that happen as side effects of the
  // initial-load cleanup and the background-poll teardown.
  //
  // Why a separate effect instead of relying on the existing bumps?
  //   • It is keyed purely on the "which market" dimension, making the intent
  //     clear and the guard independently testable.
  //   • It removes the assumption that fetchForeground changing always implies
  //     tokenAddress changed — in principle those could diverge if candlesUrl
  //     is ever memoised differently.

  useEffect(() => {
    fgRequestId.current++;
    bgRequestId.current++;
    // Reset the selected timeframe to the default (1h) so traders always see
    // the same starting view for every market.  Without this reset a user who
    // switched to 4h on market A would immediately see a 4h request for market
    // B, which is surprising and untestable without this explicit call.
    setBucket("1h");
    // No cleanup: the increment is the guard; teardown is handled by the
    // initial-load and poll effects that run after this one.
  }, [tokenAddress, chainId]);

  // ── Initial load & timeframe change ───────────────────────────────────────
  // Show the skeleton spinner so the user knows data is loading.
  // Invalidate any in-flight foreground AND background fetches before starting
  // a new cycle (background fetch for the old bucket must not overwrite the
  // new bucket's data).

  useEffect(() => {
    bgRequestId.current++; // invalidate any in-flight background fetch for the old bucket
    void fetchForeground(bucket);
    return () => {
      fgRequestId.current++; // invalidate in-flight foreground fetch
    };
  }, [fetchForeground, bucket]);

  // ── Background 30-second poll ──────────────────────────────────────────────
  // Runs silently so the chart updates without flickering.
  // Skipped while the tab is hidden OR while the chart is off-screen (e.g.
  // the user navigated to a different SPA route).  Resumes with an immediate
  // catch-up fetch when the tab/chart becomes visible again.

  useEffect(() => {
    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (!isIntersectingRef.current) return; // chart off-screen / off-route
      void fetchBackground(bucketRef.current);
    };

    const intervalId = setInterval(tick, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      // Tab just became visible — fire a catch-up fetch only if the chart is
      // also in the viewport.  `isIntersectingRef` prevents refreshing a chart
      // from a different market that is still mounted but off-screen (SPA
      // navigation scenario).  When the user scrolls back to the chart the
      // IntersectionObserver will fire its own catch-up fetch (see below).
      if (typeof document !== "undefined" && !document.hidden && isIntersectingRef.current) {
        void fetchBackground(bucketRef.current);
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      bgRequestId.current++; // invalidate any in-flight background fetch
      clearInterval(intervalId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  // fetchBackground is stable (memoised on candlesUrl only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBackground]);

  // ── Intersection Observer — pause poll when chart is off-screen ────────────
  // Handles the SPA case where the market detail page is still mounted but the
  // user has navigated away (the chart div becomes hidden / removed from view).
  // When the chart scrolls back into the viewport a catch-up fetch fires.

  useEffect(() => {
    const el = chartRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const wasIntersecting = isIntersectingRef.current;
        isIntersectingRef.current = entry.isIntersecting;

        if (!wasIntersecting && entry.isIntersecting) {
          // Chart just re-entered the viewport — catch-up fetch if tab is visible
          if (typeof document === "undefined" || !document.hidden) {
            void fetchBackground(bucketRef.current);
          }
        }
      },
      { threshold: 0 }, // fire as soon as any pixel enters / leaves the viewport
    );

    observer.observe(el);
    return () => observer.disconnect();
  // fetchBackground is stable; chartRef.current won't change after mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBackground]);

  // ── Derived Y-axis domains ─────────────────────────────────────────────────

  const allPrices = points.flatMap((p) => [p.high, p.low]).filter((v) => v > 0);
  const yMin = allPrices.length ? Math.min(...allPrices) * 0.97 : 0;
  const yMax = allPrices.length ? Math.max(...allPrices) * 1.03 : 1;

  /** Maximum volume value across all candles, used to set the volume Y-axis ceiling. */
  const volMax = points.reduce((m, p) => Math.max(m, p.volume), 0);

  /**
   * True when we have data to render.  When the volume subplot is visible the
   * price-chart XAxis labels are hidden (labels shown only at the bottom of the
   * combined panel, on the volume chart's XAxis, just like TradingView).
   */
  const showVolumePanel = points.length > 0 && !loading && !error;

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── Shared chart axes / grid ───────────────────────────────────────────────
  // Extracted so both chart modes share identical axis configuration.

  const sharedGradient = (
    <defs>
      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.18} />
        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
      </linearGradient>
    </defs>
  );

  const sharedGrid = (
    <CartesianGrid
      strokeDasharray="3 3"
      stroke="rgba(255,255,255,0.05)"
      vertical={false}
    />
  );

  /**
   * Price-chart XAxis: when the volume panel is visible we suppress the tick
   * labels here (they live at the bottom of the volume subplot instead, just
   * like a standard TradingView layout).  When there's no volume panel the
   * labels are shown as normal.
   */
  const sharedXAxis = (
    <XAxis
      dataKey="time"
      tickFormatter={(v: number) => formatTime(v, bucket)}
      tick={showVolumePanel ? false : { fontSize: 10, fill: "rgba(156,163,175,0.7)" }}
      axisLine={false}
      tickLine={false}
      minTickGap={50}
      height={showVolumePanel ? 4 : undefined}
    />
  );

  /** Volume subplot XAxis: carries the visible time labels for both panes. */
  const volumeXAxis = (
    <XAxis
      dataKey="time"
      tickFormatter={(v: number) => formatTime(v, bucket)}
      tick={{ fontSize: 10, fill: "rgba(156,163,175,0.7)" }}
      axisLine={false}
      tickLine={false}
      minTickGap={50}
    />
  );

  /** Volume subplot YAxis — right-aligned, shows nETH scale. */
  const volumeYAxis = (
    <YAxis
      yAxisId="vol"
      domain={[0, volMax * 1.15 || 1]}
      tickFormatter={(v: number) => (v === 0 ? "0" : `${formatCompact(v)} nETH`)}
      tick={{ fontSize: 9, fill: "rgba(156,163,175,0.5)" }}
      axisLine={false}
      tickLine={false}
      width={72}
      tickCount={3}
    />
  );

  const sharedYAxis = (
    <YAxis
      domain={[yMin, yMax]}
      tickFormatter={(v: number) => v.toExponential(2)}
      tick={{ fontSize: 10, fill: "rgba(156,163,175,0.7)" }}
      axisLine={false}
      tickLine={false}
      width={72}
    />
  );

  const sharedTooltip = (
    <Tooltip
      content={<ChartTooltip bucket={bucket} />}
      cursor={{ stroke: "rgba(124,58,237,0.4)", strokeWidth: 1 }}
    />
  );

  return (
    <div ref={chartRef} className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="font-display font-semibold text-sm">
            {symbol} price history
          </h3>
          {/* Last-updated indicator — hidden during the initial skeleton phase */}
          {lastUpdatedAt !== null && (
            (() => {
              const ageMs = nowMs - lastUpdatedAt;
              const isStale = ageMs > 90_000;
              return (
                <span
                  data-testid="chart-last-updated"
                  className={`flex items-center gap-1 text-[10px] font-mono transition-colors ${
                    isStale ? "text-amber-500/70" : "text-emerald-400/80"
                  }`}
                >
                  {/* Pulse dot: animates while fresh, static amber when stale */}
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      isStale
                        ? "bg-amber-500/70"
                        : "bg-emerald-400 animate-pulse"
                    }`}
                  />
                  {formatAge(lastUpdatedAt, nowMs)}
                </span>
              );
            })()
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Chart-mode toggle */}
          <div className="flex rounded-lg border border-line overflow-hidden text-xs">
            <button
              data-testid="chart-mode-candles"
              onClick={() => {
                setChartMode("candles");
                try { localStorage.setItem("synapz:chartMode", "candles"); } catch { /* ignore */ }
              }}
              className={`px-2.5 py-1 font-mono transition-colors ${
                chartMode === "candles"
                  ? "bg-plasma/15 text-plasma"
                  : "text-muted hover:text-ink"
              }`}
            >
              Candles
            </button>
            <button
              data-testid="chart-mode-line"
              onClick={() => {
                setChartMode("line");
                try { localStorage.setItem("synapz:chartMode", "line"); } catch { /* ignore */ }
              }}
              className={`px-2.5 py-1 font-mono transition-colors ${
                chartMode === "line"
                  ? "bg-plasma/15 text-plasma"
                  : "text-muted hover:text-ink"
              }`}
            >
              Line
            </button>
          </div>
          {/* Timeframe tabs */}
          <div className="flex rounded-lg border border-line overflow-hidden text-xs">
            {BUCKETS.map((b) => (
              <button
                key={b}
                onClick={() => setBucket(b)}
                className={`px-2.5 py-1 font-mono transition-colors ${
                  bucket === b
                    ? "bg-plasma/15 text-plasma"
                    : "text-muted hover:text-ink"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Price chart area */}
      <div className="h-48 w-full">
        {loading ? (
          <div data-testid="price-chart-skeleton" className="h-full rounded-lg bg-panel/60 animate-pulse" />
        ) : error ? (
          <div className="h-full flex items-center justify-center text-xs text-danger/70">
            Could not load price data
          </div>
        ) : points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted/50">
            No trades yet — chart will appear once the first trade is indexed
          </div>
        ) : chartMode === "line" ? (
          /* ── Line mode: AreaChart with close-price area only ── */
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={points}
              margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            >
              {sharedGradient}
              {sharedGrid}
              {sharedXAxis}
              {sharedYAxis}
              {sharedTooltip}
              <Area
                type="monotone"
                dataKey="price"
                stroke="#7c3aed"
                strokeWidth={1.5}
                fill="url(#priceGradient)"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          /* ── Candles mode: ComposedChart with CandlestickShape + area overlay ── */
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={points}
              margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            >
              {sharedGradient}
              {sharedGrid}
              {sharedXAxis}
              {sharedYAxis}
              {sharedTooltip}
              {/* Subtle close-price area overlay — faint trend guide */}
              <Area
                type="monotone"
                dataKey="price"
                stroke="#7c3aed"
                strokeWidth={1}
                strokeOpacity={0.4}
                fill="url(#priceGradient)"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              {/* OHLC candlestick bars */}
              <Bar
                dataKey="high"
                shape={<CandlestickShape />}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Volume subplot — only rendered when we have candle data */}
      {showVolumePanel && (
        <div data-testid="volume-panel" className="h-16 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={points}
              margin={{ top: 2, right: 4, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.03)"
                vertical={false}
              />
              {volumeXAxis}
              {volumeYAxis}
              {/* Volume bars coloured green/red per candle direction */}
              <Bar
                dataKey="volume"
                yAxisId="vol"
                shape={<VolumeBarShape />}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
